/**
 * NoteSheet — "Ask a quick question" bottom sheet / side drawer.
 *
 * Design contract §4 Global:
 *   - Slides in (.drawer — translateY on mobile, translateX on desktop),
 *     backdrop, role=dialog aria-modal, focus-trap, Esc close.
 *   - Labeled textarea (maxlength 2000 + live char count).
 *   - Amber "Send" (disabled while empty or pending).
 *   - POST /api/console/notes { body } → 201/202 → close + toast.
 *   - Error → inline alert, keeps text.
 *   - prefers-reduced-motion honored (global.css gates .drawer/.backdrop
 *     animations on html.anim-go, and the reduce-motion media query zeroes
 *     all durations — no bespoke animation strings needed here anymore).
 *
 * Triggered by: TopBar "Ask" button AND Overview "Ask a question" CTA
 * via shared NoteSheetContext.
 */
import { useState, useEffect, useRef, useCallback } from 'react';
import { X, MessageCircle, Send } from 'lucide-react';
import { apiFetch } from '../lib/api.js';
import { useNoteSheet } from '../context/NoteSheetContext.jsx';
import { useToast } from '../context/ToastContext.jsx';

const MAX_CHARS = 2000;

/**
 * Returns all keyboard-focusable elements inside a container, excluding disabled.
 */
function getFocusable(container) {
  return Array.from(
    container.querySelectorAll(
      'button:not([disabled]), [href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'
    )
  ).filter((el) => !el.hasAttribute('disabled') && el.tabIndex !== -1);
}

export default function NoteSheet() {
  const { isOpen, close } = useNoteSheet();
  const { show: showToast }  = useToast();

  const [body,     setBody]     = useState('');
  const [status,   setStatus]   = useState('idle'); // 'idle' | 'sending' | 'error'
  const [errorMsg, setErrorMsg] = useState('');

  const dialogRef  = useRef(null);
  const textareaRef = useRef(null);

  /* ── Focus textarea when sheet opens ─────────────────────────────────── */
  useEffect(() => {
    if (!isOpen) return;
    // Slight delay so the CSS animation has started before we steal focus.
    const t = setTimeout(() => textareaRef.current?.focus(), 80);
    return () => clearTimeout(t);
  }, [isOpen]);

  /* ── Reset error state when re-opened ────────────────────────────────── */
  useEffect(() => {
    if (isOpen) {
      setStatus('idle');
      setErrorMsg('');
      // Body intentionally NOT reset — keeps partial draft if re-opened.
    }
  }, [isOpen]);

  /* ── ESC to close ─────────────────────────────────────────────────────── */
  useEffect(() => {
    if (!isOpen) return;
    function onKeyDown(e) {
      if (e.key === 'Escape') { e.preventDefault(); close(); }
    }
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [isOpen, close]);

  /* ── Focus trap — Tab/Shift+Tab cycles within dialog ─────────────────── */
  useEffect(() => {
    if (!isOpen) return;
    function onKeyDown(e) {
      if (e.key !== 'Tab') return;
      const dialog = dialogRef.current;
      if (!dialog) return;
      const focusable = getFocusable(dialog);
      if (focusable.length === 0) return;
      const first = focusable[0];
      const last  = focusable[focusable.length - 1];
      if (e.shiftKey) {
        if (document.activeElement === first || !dialog.contains(document.activeElement)) {
          e.preventDefault();
          last.focus();
        }
      } else {
        if (document.activeElement === last || !dialog.contains(document.activeElement)) {
          e.preventDefault();
          first.focus();
        }
      }
    }
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [isOpen]);

  /* ── Prevent body scroll while open ──────────────────────────────────── */
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => { document.body.style.overflow = ''; };
  }, [isOpen]);

  /* ── Send ─────────────────────────────────────────────────────────────── */
  const handleSend = useCallback(async () => {
    if (!body.trim() || status === 'sending') return;
    setStatus('sending');
    setErrorMsg('');
    try {
      await apiFetch('/api/console/notes', {
        method: 'POST',
        body:   { body: body.trim() },
      });
      setBody('');
      setStatus('idle');
      close();
      showToast('Sent — Mateo will get back to you.');
    } catch (err) {
      setStatus('error');
      setErrorMsg(err.message || 'Could not send. Please try again.');
    }
  }, [body, status, close, showToast]);

  /* ── Not mounted when closed ──────────────────────────────────────────── */
  if (!isOpen) return null;

  const canSend = body.trim().length > 0 && status !== 'sending';

  return (
    <>
      {/* ── Backdrop ──────────────────────────────────────────────────────── */}
      <div className="backdrop" aria-hidden="true" onClick={close} />

      {/* ── Drawer / sheet panel ─────────────────────────────────────────── */}
      <div
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        aria-label="Ask a quick question"
        aria-labelledby="note-sheet-heading"
        className="drawer"
        style={{ paddingBottom: 'env(safe-area-inset-bottom)' }}
      >
        {/* Drag handle — decorative, mobile only */}
        <div
          aria-hidden="true"
          style={{ width: '36px', height: '4px', background: 'var(--line-strong)', margin: '10px auto 0' }}
        />

        {/* Header row */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '18px 20px 0' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
            <MessageCircle size={18} color="var(--acc)" strokeWidth={2} aria-hidden="true" />
            <h2 id="note-sheet-heading" className="d-xl" style={{ fontSize: '20px' }}>
              Ask a quick question
            </h2>
          </div>

          {/* Close button */}
          <button
            type="button"
            aria-label="Close"
            onClick={close}
            className="btnx quiet"
            style={{ height: '40px', width: '40px', padding: 0 }}
          >
            <X size={16} aria-hidden="true" />
          </button>
        </div>

        {/* Form body */}
        <div style={{ padding: '18px 20px 20px', flex: 1, display: 'flex', flexDirection: 'column' }}>
          {/* Textarea */}
          <label htmlFor="note-body" className="mlabel" style={{ display: 'block', marginBottom: '8px' }}>
            Your message
          </label>
          <textarea
            id="note-body"
            ref={textareaRef}
            className="tax"
            value={body}
            onChange={(e) => {
              setBody(e.target.value.slice(0, MAX_CHARS));
              if (status === 'error') { setStatus('idle'); setErrorMsg(''); }
            }}
            placeholder="What's on your mind? Mateo will reply by email."
            maxLength={MAX_CHARS}
            rows={5}
          />

          {/* Char count + inline error row */}
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: '6px', minHeight: '18px' }}>
            {status === 'error' ? (
              <span role="alert" style={{ fontSize: '13px', color: 'var(--bad)', fontFamily: 'var(--font-body)', lineHeight: 1.4 }}>
                {errorMsg}
              </span>
            ) : (
              <span /> /* spacer */
            )}
            <span
              className="mono"
              style={{
                fontSize: '12px',
                color: body.length >= MAX_CHARS * 0.9 ? 'var(--acc-ink)' : 'var(--ink-soft)',
                marginLeft: 'auto',
                flexShrink: 0,
              }}
            >
              {body.length}/{MAX_CHARS}
            </span>
          </div>

          {/* Action buttons */}
          <div style={{ display: 'flex', gap: '12px', marginTop: '16px' }}>
            <button type="button" className="btnx quiet" style={{ flex: 1, height: '48px' }} onClick={close}>
              Cancel
            </button>

            <button
              type="button"
              className="btnx primary"
              style={{ flex: 2, height: '48px' }}
              onClick={handleSend}
              disabled={!canSend}
              aria-disabled={!canSend}
            >
              {status === 'sending' ? (
                'Sending…'
              ) : (
                <>
                  <Send size={15} aria-hidden="true" />
                  Send
                </>
              )}
            </button>
          </div>
        </div>
      </div>
    </>
  );
}
